import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { ChildSelectorBar } from '../components/ChildSelectorBar';
import { Lightbulb, Clock, Target, Info, ArrowRight, Star, Zap, History } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { DjayAvatar, DjayBubble } from '../../../mascot';
import { MODULES } from '../../../content';
import { getWeeklyActivityForLearner } from '../../../utils/activityHelpers';
import { hasActiveDecay, getDecayStatus } from '../../../mastery';
import { type FamilyMasteryState } from '../../../mastery';
import { type LearnerAccount } from '../../../accounts';
import { AppState } from '../../../app/types';
interface TipsPageProps {
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  childData: AppState | null;
  getLearnerModuleMastery: (learnerId: string, moduleId: string) => number;
  setViewingLearner: (id: string) => void;
  onNavigate?: (route: string) => void;
}
export function TipsPage({
  linkedLearners,
  selectedChild,
  childData,
  getLearnerModuleMastery,
  setViewingLearner,
  onNavigate
}: TipsPageProps) {
  const tips = useMemo(() => {
    if (!selectedChild || !childData) return [];
    const generatedTips = [];
    // 1. Rhythm Tip
    const weeklyActivity = getWeeklyActivityForLearner(childData.questionStates || {});
    const daysPracticed = weeklyActivity.filter(d => d.practiced).length;
    if (daysPracticed === 0) {
      generatedTips.push({
        id: 'rhythm-none',
        icon: <Clock className="w-6 h-6 text-amber-500" data-id="element-4819" />,
        title: 'Start met een vast ritme',
        content: `Probeer een vast moment op de dag te vinden voor ${selectedChild.displayName} om te oefenen. Bijvoorbeeld 10 minuten na het eten. Korte, regelmatige sessies werken veel beter dan één lange sessie in het weekend.`,
        color: 'amber' as const
      });
    } else if (daysPracticed < 3) {
      generatedTips.push({
        id: 'rhythm-some',
        icon: <Clock className="w-6 h-6 text-indigo-500" data-id="element-4820" />,
        title: 'Bouw het ritme uit',
        content: `${selectedChild.displayName} heeft deze week ${daysPracticed} keer geoefend. Dat is een mooi begin! Probeer toe te werken naar 3 of 4 keer per week voor het beste leereffect.`,
        color: 'indigo' as const
      });
    } else {
      generatedTips.push({
        id: 'rhythm-good',
        icon: <Clock className="w-6 h-6 text-emerald-500" data-id="element-4821" />,
        title: 'Houd dit ritme vast!',
        content: `Fantastisch! ${selectedChild.displayName} oefent heel regelmatig. Dit is de perfecte manier om nieuwe stof eigen te maken en te onthouden. Blijf dit stimuleren.`,
        color: 'emerald' as const
      });
    }
    // 2. Subject Tip (Weakest)
    let weakestModule = null;
    let lowestMastery = 100;
    let strongestModule = null;
    let highestMastery = 0;
    MODULES.forEach(module => {
      const mastery = getLearnerModuleMastery(selectedChild.id, module.id);
      if (mastery > 0 && mastery < lowestMastery) {
        lowestMastery = mastery;
        weakestModule = module;
      }
      if (mastery > highestMastery) {
        highestMastery = mastery;
        strongestModule = module;
      }
    });
    if (weakestModule && lowestMastery < 50) {
      generatedTips.push({
        id: 'subject-weak',
        icon: <Target className="w-6 h-6 text-rose-500" data-id="element-4822" />,
        title: `Extra aandacht voor ${weakestModule.title.toLowerCase()}`,
        content: `Het vak ${weakestModule.title.toLowerCase()} is nog wat lastig (${lowestMastery}% beheersing). Meester djay zal hier automatisch makkelijkere vragen over stellen om het vertrouwen op te bouwen. Je kunt helpen door in het dagelijks leven samen sommetjes of taalpuzzels te maken die hierbij passen.`,
        color: 'rose' as const,
        action: {
          label: 'Bekijk voortgang',
          onClick: () => onNavigate?.('voortgang')
        }
      });
    }
    // 3. Strongest Module Praise
    if (strongestModule && highestMastery >= 70) {
      generatedTips.push({
        id: 'subject-strong',
        icon: <Star className="w-6 h-6 text-emerald-500" data-id="element-4823" />,
        title: `Uitblinker in ${strongestModule.title.toLowerCase()}!`,
        content: `${selectedChild.displayName} gaat ontzettend goed met ${strongestModule.title.toLowerCase()} (${highestMastery}% beheersing). Geef een complimentje! Succeservaringen zijn heel belangrijk voor de motivatie.`,
        color: 'emerald' as const
      });
    }
    // 4. Streak Tip
    const currentStreak = childData.stats?.streakState?.currentStreak || 0;
    if (currentStreak >= 3) {
      generatedTips.push({
        id: 'streak-active',
        icon: <Zap className="w-6 h-6 text-amber-500" data-id="element-4824" />,
        title: `${currentStreak} dagen op rij geoefend!`,
        content: `Wat een inzet! ${selectedChild.displayName} heeft een 'streak' van ${currentStreak} dagen opgebouwd. Probeer deze reeks niet te verbreken, dat werkt enorm motiverend.`,
        color: 'amber' as const
      });
    }
    // 5. Mastery Decay Tips — use actual decay engine
    // Group families by module, find modules with decaying families
    if (childData.familyMastery) {
      const now = Date.now();
      const decayingModules: Array<{
        module: (typeof MODULES)[number];
        decayingCount: number;
        worstStatus: 'fading' | 'stale';
        daysSince: number;
      }> = [];
      for (const module of MODULES) {
        const mastery = getLearnerModuleMastery(selectedChild.id, module.id);
        if (mastery <= 0) continue;
        let moduleDecayingCount = 0;
        let moduleWorstStatus: 'fading' | 'stale' = 'fading';
        let oldestLastSeen = Infinity;
        for (const [key, state] of Object.entries(childData.familyMastery)) {
          // Match families to this module by checking the key prefix
          const familyState = state as FamilyMasteryState;
          if (!key.startsWith(module.id + '-') && !key.includes('-' + module.id.toLowerCase() + '-')) continue;
          if (!hasActiveDecay(familyState, now)) continue;
          moduleDecayingCount++;
          const status = getDecayStatus(familyState, now);
          if (status === 'stale') moduleWorstStatus = 'stale';
          if (familyState.lastSeen < oldestLastSeen) {
            oldestLastSeen = familyState.lastSeen;
          }
        }
        if (moduleDecayingCount > 0) {
          const daysSince = oldestLastSeen < Infinity ? Math.floor((now - oldestLastSeen) / (1000 * 60 * 60 * 24)) : 0;
          decayingModules.push({
            module,
            decayingCount: moduleDecayingCount,
            worstStatus: moduleWorstStatus,
            daysSince
          });
        }
      }
      // Sort: stale first, then by days since (most stale first)
      decayingModules.sort((a, b) => {
        if (a.worstStatus !== b.worstStatus) {
          return a.worstStatus === 'stale' ? -1 : 1;
        }
        return b.daysSince - a.daysSince;
      });
      // Show up to 2 decay tips
      decayingModules.slice(0, 2).forEach((dm, idx) => {
        const isStale = dm.worstStatus === 'stale';
        const weeksAgo = Math.floor(dm.daysSince / 7);
        const timeLabel = weeksAgo >= 4 ? `meer dan een maand` : weeksAgo >= 2 ? `${weeksAgo} weken` : `meer dan een week`;
        generatedTips.push({
          id: `mastery-decay-${idx}`,
          icon: <History className={`w-6 h-6 ${isStale ? 'text-rose-500' : 'text-indigo-500'}`} data-id="element-4825" />,
          title: isStale ? `${dm.module.title} dreigt weg te zakken` : `Tijd voor herhaling: ${dm.module.title.toLowerCase()}`,
          content: isStale ? `${selectedChild.displayName} heeft al ${timeLabel} niet geoefend met ${dm.module.title.toLowerCase()}. De kennis begint weg te zakken. Even 10 minuten oefenen kan al veel helpen!` : `Het is ${timeLabel} geleden dat ${selectedChild.displayName} heeft geoefend met ${dm.module.title.toLowerCase()}. Regelmatig herhalen helpt om de stof goed te onthouden.`,
          color: (isStale ? 'rose' : 'indigo') as const,
          action: {
            label: 'Bekijk vakken',
            onClick: () => onNavigate?.('voortgang')
          }
        });
      });
    }
    // 6. General Platform Tip
    generatedTips.push({
      id: 'platform-info',
      icon: <Info className="w-6 h-6 text-sky-500" data-id="element-4826" />,
      title: 'Hoe Meester djay werkt',
      content: 'Het systeem past zich automatisch aan het niveau van je kind aan. Als het goed gaat, worden de vragen uitdagender. Bij fouten doet het systeem een stapje terug en geeft het extra uitleg. Fouten maken is hier dus letterlijk onderdeel van het leerproces!',
      color: 'sky' as const
    });
    return generatedTips;
  }, [selectedChild, childData, getLearnerModuleMastery, onNavigate]);
  if (!selectedChild) {
    return <PageLayout title="Tips" description="Persoonlijk advies van Djay" data-id="element-4827">
        <div className="max-w-4xl mx-auto pb-12" data-id="element-4828">
          <div className="flex flex-col items-center justify-center py-20 px-4 text-center" data-id="element-4829">
            <DjayAvatar size="lg" emotion="thinking" animate data-id="element-4830" />
            <h2 className="text-xl font-bold text-slate-900 mt-6 mb-2" data-id="element-4831">
              Koppel eerst een kind
            </h2>
            <p className="text-slate-500 max-w-md" data-id="element-4832">
              Om persoonlijke tips te kunnen geven, moet je eerst een kind
              koppelen. Ga naar Instellingen om een kind te koppelen met een
              koppelcode.
            </p>
          </div>
        </div>
      </PageLayout>;
  }
  return <PageLayout title="Tips" description="Persoonlijk advies van Djay" data-id="element-4833">
      <div className="max-w-5xl mx-auto space-y-8 pb-12" data-id="element-4834">
        <ChildSelectorBar linkedLearners={linkedLearners} selectedChildId={selectedChild.id} onSelect={setViewingLearner} data-id="element-4835" />

        <div className="flex items-center gap-4 mb-6" data-id="element-4836">
          <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center border border-emerald-100 overflow-hidden flex-shrink-0" data-id="element-4837">
            <DjayAvatar size="md" emotion="thinking" animate data-id="element-4838" />
          </div>
          <div className="flex-1" data-id="element-4839">
            <h2 className="text-lg font-black text-slate-900 mb-1" data-id="element-4840">
              Persoonlijke tips voor {selectedChild.displayName}
            </h2>
            <p className="text-sm text-slate-600" data-id="element-4841">
              Ik heb het leergedrag geanalyseerd. Hier zijn mijn aanbevelingen!
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4842">
          {tips.map((tip, i) => {
          const colorClasses = {
            amber: 'bg-amber-50 border-amber-200 text-amber-900',
            indigo: 'bg-indigo-50 border-indigo-200 text-indigo-900',
            emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
            rose: 'bg-rose-50 border-rose-200 text-rose-900',
            sky: 'bg-sky-50 border-sky-200 text-sky-900'
          };
          const iconBgClasses = {
            amber: 'bg-amber-100',
            indigo: 'bg-indigo-100',
            emerald: 'bg-emerald-100',
            rose: 'bg-rose-100',
            sky: 'bg-sky-100'
          };
          const actionTextClasses = {
            amber: 'text-amber-700 hover:text-amber-800',
            indigo: 'text-indigo-700 hover:text-indigo-800',
            emerald: 'text-emerald-700 hover:text-emerald-800',
            rose: 'text-rose-700 hover:text-rose-800',
            sky: 'text-sky-700 hover:text-sky-800'
          };
          return <motion.div key={tip.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.1
          }} data-id="element-4843">
                <Card className={`p-6 border h-full flex flex-col ${colorClasses[tip.color]} shadow-sm`} data-id="element-4844">
                  <div className="flex items-center gap-4 mb-4" data-id="element-4845">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBgClasses[tip.color]}`} data-id="element-4846">
                      {tip.icon}
                    </div>
                    <h3 className="text-lg font-black leading-tight" data-id="element-4847">
                      {tip.title}
                    </h3>
                  </div>
                  <p className="text-sm font-medium opacity-80 leading-relaxed flex-1" data-id="element-4848">
                    {tip.content}
                  </p>
                  {tip.action && <div className="mt-4 pt-4 border-t border-black/5" data-id="element-4849">
                      <button onClick={tip.action.onClick} className={`text-sm font-bold flex items-center gap-1 transition-colors ${actionTextClasses[tip.color]}`} data-id="element-4850">
                        {tip.action.label}
                        <ArrowRight className="w-4 h-4" data-id="element-4851" />
                      </button>
                    </div>}
                </Card>
              </motion.div>;
        })}
        </div>

        <Card className="p-8 border-slate-200 shadow-sm bg-white mt-8 text-center" data-id="element-4852">
          <Lightbulb className="w-10 h-10 text-amber-400 mx-auto mb-4" data-id="element-4853" />
          <h3 className="text-xl font-black text-slate-900 mb-2" data-id="element-4854">
            Heb je vragen?
          </h3>
          <p className="text-slate-600 mb-6 max-w-lg mx-auto" data-id="element-4855">
            De docent van {selectedChild.displayName} heeft ook toegang tot deze
            gegevens en kan je meer vertellen over de voortgang in de klas.
          </p>
        </Card>
      </div>
    </PageLayout>;
}